import React, { useEffect, useState } from 'react';
import {
  Box,
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { MANUAL_REQUEST_BASE_CODE, MANUAL_REQUEST_DROPDOWNS, PRODUCT_CATEGORIES } from '../../constants/constant';
import { getDataWAuth } from '../../utils/api-service';
import { getProductType } from '../../utils/utils';

const formatAmount = (item) => `${Number(item.amountInDocCurrency || 0).toLocaleString()} ${item.currencyKey || ''}`.trim();

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const FinalManualRequestModal = ({ open, selectedLineItem, onBack, onClose, onConfirm, isSaving }) => {
  const [extensionType, setExtensionType] = useState('IE');
  const [financingAmount, setFinancingAmount] = useState('');
  const [financingAmountError, setFinancingAmountError] = useState('');
  const [referenceContract, setReferenceContract] = useState('');
  const [referenceContractError, setReferenceContractError] = useState('');
  const [financingType, setFinancingType] = useState(MANUAL_REQUEST_DROPDOWNS.financingTypes[1].value);
  const [baseCodePresent, setBaseCodePresent] = useState(null);
  const [baseCodeCheckError, setBaseCodeCheckError] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productCategoryError, setProductCategoryError] = useState('');
  const [customerNameTI, setCustomerNameTI] = useState('');
  const [customerNameTIError, setCustomerNameTIError] = useState('');
  const [serialNumberTI, setSerialNumberTI] = useState('');
  const [serialNumberTIError, setSerialNumberTIError] = useState('');

  const baseCode = selectedLineItem.baseCode?.trim() || '';
  const materialType = productCategory ? getProductType(productCategory) : '';
  const isCheckingBaseCode = extensionType === 'IE' && baseCodePresent === null && !!baseCode;

  useEffect(() => {
    setExtensionType('IE');
    setFinancingAmount('');
    setFinancingAmountError('');
    setReferenceContract('');
    setReferenceContractError('');
    setFinancingType(MANUAL_REQUEST_DROPDOWNS.financingTypes[1].value);
    setBaseCodePresent(null);
    setBaseCodeCheckError('');
    setProductCategory('');
    setProductCategoryError('');
    setCustomerNameTI('');
    setCustomerNameTIError('');
    setSerialNumberTI('');
    setSerialNumberTIError('');
  }, [open, selectedLineItem]);

  useEffect(() => {
    const checkBaseCode = async () => {
      if (!open || extensionType !== 'IE') {
        return;
      }

      if (!baseCode) {
        setBaseCodePresent(false);
        setBaseCodeCheckError('No base code was received from SAP.');
        return;
      }

      try {
        setBaseCodePresent(null);
        setBaseCodeCheckError('');
        const response = await getDataWAuth(MANUAL_REQUEST_BASE_CODE, { baseCode });
        setBaseCodePresent(response?.data?.present === true);
      } catch (error) {
        setBaseCodePresent(null);
        setBaseCodeCheckError(error.response?.data?.message || 'Unable to check the base code. Try again before confirming.');
      }
    };

    checkBaseCode();
  }, [baseCode, extensionType, open]);

  const handleConfirm = () => {
    let hasValidationError = false;

    if ((extensionType === 'EF' || extensionType === 'TI') && (!financingAmount || !Number.isFinite(Number(financingAmount)) || Number(financingAmount) <= 0)) {
      setFinancingAmountError('Financing Amount must be greater than 0.');
      hasValidationError = true;
    }

    if (extensionType === 'EF' && !referenceContract.trim()) {
      setReferenceContractError('Financing Reference is required.');
      hasValidationError = true;
    }

    if (extensionType === 'EF' && !financingType) {
      hasValidationError = true;
    }

    if (extensionType === 'IE' && baseCodePresent === false && !productCategory) {
      setProductCategoryError('Product Category is required to add the base code.');
      hasValidationError = true;
    }

    if (extensionType === 'IE' && baseCodePresent === null) {
      setBaseCodeCheckError('Wait for the base code check to finish before confirming.');
      hasValidationError = true;
    }

    // if (extensionType === 'TI' && (!financingAmount || !Number.isFinite(Number(financingAmount)) || Number(financingAmount) <= 0)) {
    //   setFinancingAmountError('Amount must be greater than 0.');
    //   hasValidationError = true;
    // }

    if (extensionType === 'TI' && !customerNameTI.trim()) {
      setCustomerNameTIError('Customer Name is required.');
      hasValidationError = true;
    }
    if (extensionType === 'TI' && !serialNumberTI.trim()) {
      setSerialNumberTIError('Serial Number is required.');
      hasValidationError = true;
    }

    if (hasValidationError) {
      return;
    }

    const payload = {
      ...selectedLineItem,
      paymentBlockKey: 'B',
      extensionType,
    };

    if (extensionType === 'EF') {
      Object.assign(payload, {
        financingAmount,
        referenceContract,
        financingType,
      });
    }

    if (extensionType === 'TI') {
      Object.assign(payload, {
        financingAmount,
        customerNameTI,
        serialNumberTI,
      });
    }

    const newProductType = extensionType === 'IE' && baseCodePresent === false
      ? { baseCode, productCategory, materialType }
      : null;

    onConfirm(payload, newProductType);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: { borderRadius: '8px', margin: '24px', maxWidth: '696px', width: '100%' } }}>
      <DialogTitle sx={{ alignItems: 'center', display: 'flex', fontSize: '20px', fontWeight: 700, justifyContent: 'space-between', padding: '24px' }}>
        New Request
        <Button aria-label="Close" onClick={onClose} sx={{ color: '#616161', minWidth: 0, padding: 0 }}><CloseIcon /></Button>
      </DialogTitle>
      <DialogContent sx={{ padding: '0 24px' }}>
        <Box sx={{ borderBottom: '1px solid #c7c7c7', display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(3, 1fr)', paddingBottom: '18px' }}>
          <Box><Typography sx={{ fontWeight: 700 }}>Company Code:</Typography><Typography>{selectedLineItem.companyCode || '-'}</Typography></Box>
          <Box><Typography sx={{ fontWeight: 700 }}>Customer Number:</Typography><Typography>{selectedLineItem.customerNo || '-'}</Typography></Box>
          <Box><Typography sx={{ fontWeight: 700 }}>Assignment Number:</Typography><Typography>{selectedLineItem.assignmentNo || '-'}</Typography></Box>
        </Box>
        <Box sx={{ borderBottom: '1px solid #c7c7c7', display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(3, 1fr)', padding: '16px 0' }}>
          <Box><Typography sx={{ fontWeight: 700 }}>Amount in doc currency:</Typography><Typography>{formatAmount(selectedLineItem)}</Typography></Box>
          <Box><Typography sx={{ fontWeight: 700 }}>Reason Code:</Typography><Typography>{selectedLineItem.reasonCode || '-'}</Typography></Box>
          <Box><Typography sx={{ fontWeight: 700 }}>Due date for net payment</Typography><Typography>{formatDate(selectedLineItem.dueDateForNetPayment)}</Typography></Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '18px' }}>
          <FormControl>
            <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, position: 'static', transform: 'none' }}>Extension Type</InputLabel>
            <RadioGroup row value={extensionType} onChange={(event) => setExtensionType(event.target.value)}>
              <FormControlLabel value="IE" control={<Radio size="small" />} label="IE" />
              <FormControlLabel value="EF" control={<Radio size="small" />} label="EF" />
              <FormControlLabel value="TI" control={<Radio size="small" />} label="TI" />
            </RadioGroup>
          </FormControl>
          {extensionType === 'IE' && (
            <>
              {baseCodeCheckError && <Alert severity="warning">{baseCodeCheckError}</Alert>}
              {baseCodePresent === false && baseCode && (
                <Alert severity="info">This Base Code is not configured. Select the appropriate Product Category and Material Type before saving the manual request.</Alert>
              )}
              {baseCodePresent === false && (
                <>
                  <Box>
                    <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Base Code</InputLabel>
                    <TextField fullWidth disabled size="small" value={baseCode} />
                  </Box>
                  <Box>
                    <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Product Category</InputLabel>
                    <FormControl fullWidth required size="small" error={!!productCategoryError}>
                      <Select
                        value={productCategory}
                        onChange={(event) => {
                          setProductCategory(event.target.value);
                          setProductCategoryError('');
                        }}
                      >
                        {PRODUCT_CATEGORIES.map((category) => <MenuItem key={category} value={category}>{category}</MenuItem>)}
                      </Select>
                      {productCategoryError && <Typography color="error" sx={{ fontSize: '12px', marginTop: '4px' }}>{productCategoryError}</Typography>}
                    </FormControl>
                  </Box>
                  <Box>
                    <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Material Type</InputLabel>
                    <TextField fullWidth disabled size="small" value={materialType} />
                  </Box>
                </>
              )}
            </>
          )}
          {extensionType === 'EF' && (
            <>
              <Box>
                <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Financing Amount</InputLabel>
                <TextField
                  fullWidth
                  size="small"
                  required
                  type="number"
                  value={financingAmount}
                  onChange={(event) => {
                    setFinancingAmount(event.target.value);
                    setFinancingAmountError('');
                  }}
                  inputProps={{ min: 0.01, step: 'any' }}
                  error={!!financingAmountError}
                  helperText={financingAmountError}
                />
              </Box>
              <Box>
                <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Financing Reference</InputLabel>
                <TextField
                  fullWidth
                  size="small"
                  required
                  value={referenceContract}
                  onChange={(event) => {
                    setReferenceContract(event.target.value);
                    setReferenceContractError('');
                  }}
                  error={!!referenceContractError}
                  helperText={referenceContractError}
                />
              </Box>
              <Box>
                <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Financing Type</InputLabel>
                <FormControl fullWidth required size="small">
                  <Select value={financingType} onChange={(event) => setFinancingType(event.target.value)}>
                    {MANUAL_REQUEST_DROPDOWNS.financingTypes.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </>
          )}
          {extensionType === 'TI' && (
            <>
            <Box>
              <InputLabel sx={{ color: '#333333', fontSize: '14px', fontWeight: 600, marginBottom: '6px' }}>Amount</InputLabel>
              <TextField
                fullWidth
                size="small"
                required
                value={financingAmount}
                type="number"
                onChange={(event) => {
                  setFinancingAmount(event.target.value);
                  setFinancingAmountError('');
                }}
                error={!!financingAmountError}
                helperText={financingAmountError}
              />
            </Box>
            <Box>
              <InputLabel >Customer Name</InputLabel>
              <TextField
                fullWidth
                size="small"
                required
                value={customerNameTI}
                onChange={(event) => {
                  setCustomerNameTI(event.target.value);
                  setCustomerNameTIError('');
                }}
                error={!!customerNameTIError}
                helperText={customerNameTIError}
              />
            </Box>
            <Box>
              <InputLabel >Serial Number</InputLabel>
              <TextField
                fullWidth
                size="small"
                required
                value={serialNumberTI}
                onChange={(event) => {
                  setSerialNumberTI(event.target.value);
                  setSerialNumberTIError('');
                }}
                error={!!serialNumberTIError}
                helperText={serialNumberTIError}
              />
            </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: '10px', padding: '36px 24px 16px' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined" disabled={isSaving}>Back</Button>
        <Button
          startIcon={isCheckingBaseCode ? <CircularProgress color="inherit" size={18} /> : <CheckIcon />}
          onClick={handleConfirm}
          variant="contained"
          disabled={isSaving || (extensionType === 'IE' && baseCodePresent === null)}
          sx={{ backgroundColor: '#ffd800', color: '#000', fontWeight: 600, '&:hover': { backgroundColor: '#e6c200' } }}
        >
          {isSaving ? 'Saving...' : isCheckingBaseCode ? 'Searching base code...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalManualRequestModal;